import React, { useEffect, useRef } from 'react';
import { Notice } from './notice';
import { FaEdit, FaSave, FaTimes, FaCopy, FaTrash } from 'react-icons/fa';

interface NoticeItemProps {
    notice: Notice;
    handleNoticeSave: (editedNotice: Notice) => void;
    handleNoticeCancel: () => void;
    handleNoticeEdit: (noticeId: string | null) => void;
    handleNoticeCopy: (notice: Notice) => void;
    handleNoticeDelete: (noticeId: string) => void;
    isEditMode: boolean;
    disableEditButton: boolean;
    expandedItemId: string | null;
    setExpandedItemId: (id: string | null) => void;
    disabled: boolean; // Add disabled prop
}

const NoticeItem: React.FC<NoticeItemProps> = ({ notice, handleNoticeSave, handleNoticeCancel, handleNoticeEdit, handleNoticeCopy, handleNoticeDelete, isEditMode, disableEditButton, expandedItemId, setExpandedItemId, disabled }) => {
    const [isEditing, setIsEditing] = React.useState(isEditMode);
    const [editedNotice, setEditedNotice] = React.useState<Notice>({
        ...notice,
        notice: notice.notice.replace(/<br\s*\/?>/gi, '\n'), // Convert <br> to carriage return
    });

    const itemRef = useRef<HTMLDivElement>(null);

    const itemBackgroundColor = React.useMemo(() => {
        const endDate = new Date(editedNotice.end_date).setHours(0, 0, 0, 0);
        const currentDate = new Date().setHours(0, 0, 0, 0);
        if (isEditing) {
            return 'editing';
        } else if (endDate < currentDate) {
            return 'inactive';
        } else if (editedNotice.isActive) {
            return 'active';
        } else {
            return 'inactive';
        }
    }, [isEditing, editedNotice.isActive, editedNotice.end_date]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isEditMode) {
                handleCancel();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isEditMode, handleNoticeCancel]);

    useEffect(() => {
        if (isEditMode) {
            setExpandedItemId(notice.id);
        }
    }, [isEditMode, notice.id, setExpandedItemId]);

    useEffect(() => {
        if (expandedItemId === notice.id && itemRef.current && itemRef.current.scrollIntoView) {
            itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [expandedItemId, notice.id]);

    const handleEdit = () => {
        setIsEditing(true);
        handleNoticeEdit(notice.id);
    };

    const handleSave = async () => {
        setIsEditing(false);
        const noticeToSave = {
            ...editedNotice,
            notice: editedNotice.notice.replace(/\n/g, '<br>'), // Convert carriage return to <br>
        };
        handleNoticeSave(noticeToSave);
        handleNoticeEdit(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedNotice(prevState => ({
            ...prevState,
        }));
        handleNoticeCancel();
        handleNoticeEdit(null);
    };

    const handleChange = (name: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        setEditedNotice(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleInputChange = (name: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setEditedNotice(prevState => ({
            ...prevState,
            [name]: newValue
        }));
    };

    const handleCopy = () => {
        const newNotice = {
            ...notice,
            notice_name: `${notice.notice_name} copy`,
            start_date: new Date().toISOString().split('T')[0],
            end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        };
        handleNoticeCopy(newNotice);
    };

    const handleDelete = () => {
        if (window.confirm(`Are you sure you want to delete the notice: ${notice.notice_name}?`)) {
            handleNoticeDelete(notice.id);
        }
    };

    const toggleExpand = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (!isEditing && !disableEditButton && !disabled) {
            e.stopPropagation(); // Prevent the click event from propagating
            setExpandedItemId(expandedItemId === notice.id ? null : notice.id);
        }
    };

    useEffect(() => {
        if (expandedItemId === notice.id && itemRef.current) {
            itemRef.current.focus();
        }
    }, [expandedItemId, notice.id]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, onClick: () => void) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onClick();
        }
    };

    const handleTextAreaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.stopPropagation();
        }
    };

    return (
        <div tabIndex={0} role="button"
            ref={itemRef} className={`market-item-container ${itemBackgroundColor} ${expandedItemId === notice.id ? 'expanded' : 'collapsed'}`}
            onClick={toggleExpand}
            onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                    toggleExpand(e as React.KeyboardEvent);
                }
            }}
        >
            <div
                className="notice-name"
            >
                {isEditing ? (
                    <input
                        type="text"
                        value={editedNotice.notice_name}
                        onChange={(e) => handleChange("notice_name", e)}
                        disabled={disabled} // Disable input if NoticeList is disabled
                    />
                ) : (
                    notice.notice_name
                )}
            </div>
            {expandedItemId === notice.id && (
                <>
                    <div>
                        <label htmlFor="start_date">Start:</label>
                        <input type="date" id="start_date" name="start_date" value={isEditing ? editedNotice.start_date : notice.start_date} onChange={(e) => handleChange("start_date", e)} disabled={!isEditing || disabled} />
                    </div>
                    <div>
                        <label htmlFor="end_date">End:</label>
                        <input type="date" id="end_date" name="end_date" value={isEditing ? editedNotice.end_date : notice.end_date} onChange={(e) => handleChange("end_date", e)} disabled={!isEditing || disabled} />
                    </div>
                    <div>
                        <label htmlFor="isActive">Active:</label>
                        <input type="checkbox" id="isActive" name="isActive" checked={isEditing ? editedNotice.isActive : notice.isActive} onChange={(e) => handleInputChange("isActive", e)} disabled={!isEditing || disabled} />
                    </div>
                    <div className="multiline-textarea">
                        <label htmlFor="notice">Notice:</label>
                        <textarea
                            id="notice"
                            name="notice"
                            value={editedNotice.notice} onChange={(e) => handleChange("notice", e)} disabled={!isEditing || disabled}
                            rows={8}
                            onKeyDown={handleTextAreaKeyDown}
                        />
                    </div>
                    <div className="button-container">
                        {isEditing ? (
                            <>
                                <button onClick={() => handleSave()} disabled={disabled} onKeyDown={(e) => handleKeyDown(e, handleSave)}>
                                    <FaSave className="icon" /> Save
                                </button>
                                <button onClick={() => handleCancel()} disabled={disabled} onKeyDown={(e) => handleKeyDown(e, handleCancel)}>
                                    <FaTimes className="icon" /> Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => handleEdit()} disabled={disableEditButton || disabled} onKeyDown={(e) => handleKeyDown(e, handleEdit)}>
                                    <FaEdit className="icon" /> Edit
                                </button>
                                <button onClick={() => handleCopy()} disabled={disabled} onKeyDown={(e) => handleKeyDown(e, handleCopy)}>
                                    <FaCopy className="icon" /> Copy
                                </button>
                                <button onClick={() => handleDelete()} disabled={disabled} onKeyDown={(e) => handleKeyDown(e, handleDelete)}>
                                    <FaTrash className="icon" /> Delete
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    )
};

export default NoticeItem;
